import { c as _c } from "react/compiler-runtime";
import { AmountStep } from '@src/components/sco/Funding/FundingLandingDiagnostic/AmountStep';
import { AnalyzingStep } from '@src/components/sco/Funding/FundingLandingDiagnostic/AnalyzingStep';
import { DiagnosticShell } from '@src/components/sco/Funding/FundingLandingDiagnostic/DiagnosticShell';
import { RegisterStep } from '@src/components/sco/Funding/FundingLandingDiagnostic/RegisterStep';
import { ResultsStep } from '@src/components/sco/Funding/FundingLandingDiagnostic/ResultsStep';
import { StartupStep } from '@src/components/sco/Funding/FundingLandingDiagnostic/StartupStep';
import { TractionStep } from '@src/components/sco/Funding/FundingLandingDiagnostic/TractionStep';
import { PRODUCT_STAGES } from '@src/components/sco/Funding/FundingLandingDiagnostic/types';
import { RequestFunnelContextProvider, useRequestFunnelContext } from '@src/components/sco/RequestFunnel/RequestFunnelContext';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
export type FundingLandingDiagnosticProps = {
  funnelIdentifier?: string;
};

/** Imperative API for mid-page CTAs that open the diagnostic as a modal. */
export type LandingDiagnosticHandle = {
  open: () => void;
};
const FundingLandingDiagnosticInner = forwardRef<LandingDiagnosticHandle>(function FundingLandingDiagnosticInner(_props, ref) {
  const $ = _c(32);
  const {
    step,
    setStep
  } = useRequestFunnelContext();
  const activeStep = step || "start";
  const [forceModal, setForceModal] = useState(false);
  const focused = forceModal || activeStep !== "start";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["startup", "traction", "results", "register"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const showBack = t0.includes(activeStep);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => ({
      open: () => {
        setForceModal(true);
      }
    });
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useImperativeHandle(ref, t1, t2);
  let t3;
  if ($[3] !== activeStep || $[4] !== setStep) {
    t3 = () => {
      if (activeStep === "results") {
        setStep("traction");
        return;
      }
      if (activeStep === "register") {
        setStep("results");
        return;
      }
      if (activeStep === "traction") {
        setStep("startup");
        return;
      }
      if (activeStep === "startup") {
        setStep("start");
      }
    };
    $[3] = activeStep;
    $[4] = setStep;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const onBack = t3;
  let t4;
  if ($[6] !== setStep) {
    t4 = () => {
      setForceModal(false);
      setStep("start");
    };
    $[6] = setStep;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onDismiss = t4;
  let t5;
  if ($[8] !== activeStep || $[9] !== step) {
    t5 = (activeStep === "start" || !step) && <AmountStep />;
    $[8] = activeStep;
    $[9] = step;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== activeStep) {
    t6 = activeStep === "startup" && <StartupStep />;
    $[11] = activeStep;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== activeStep) {
    t7 = activeStep === "traction" && <TractionStep />;
    $[13] = activeStep;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== activeStep) {
    t8 = activeStep === "analyzing" && <AnalyzingStep />;
    $[15] = activeStep;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== activeStep) {
    t9 = activeStep === "results" && <ResultsStep />;
    $[17] = activeStep;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== activeStep) {
    t10 = activeStep === "register" && <RegisterStep />;
    $[19] = activeStep;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== focused || $[22] !== onBack || $[23] !== onDismiss || $[24] !== showBack || $[25] !== t10 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t11 = <DiagnosticShell focused={focused} showBack={showBack} onBack={onBack} onDismiss={onDismiss}>{t5}{t6}{t7}{t8}{t9}{t10}</DiagnosticShell>;
    $[21] = focused;
    $[22] = onBack;
    $[23] = onDismiss;
    $[24] = showBack;
    $[25] = t10;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
});

/**
 * Compact inline funding diagnostic for the Funding LP hero.
 * Persists answers on a SignupRequest (including additionalFields) and loads
 * investor matches via scoInvestorSearchDraft.
 *
 * Call `ref.open()` from mid-page CTAs to present the same diagnostic as a modal.
 */
export const FundingLandingDiagnostic = forwardRef<LandingDiagnosticHandle, FundingLandingDiagnosticProps>(function FundingLandingDiagnostic(t0, ref) {
  const $ = _c(9);
  const {
    funnelIdentifier: t1
  } = t0;
  const funnelIdentifier = t1 === undefined ? "funding-landing-diagnostic" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      hasRaisedFunding: false,
      hasRevenue: false,
      productStage: PRODUCT_STAGES[2]
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== funnelIdentifier) {
    t3 = {
      groupRequestTypeKey: "funding",
      fundingGoalInDollars: 500000,
      funnelIdentifier,
      additionalFields: t2
    };
    $[1] = funnelIdentifier;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== ref) {
    t4 = <FundingLandingDiagnosticInner ref={ref} />;
    $[3] = ref;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== funnelIdentifier || $[6] !== t3 || $[7] !== t4) {
    t5 = <RequestFunnelContextProvider funnelIdentifier={funnelIdentifier} saveOnStepChange={true} initialSignupRequest={t3}>{t4}</RequestFunnelContextProvider>;
    $[5] = funnelIdentifier;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
});
export default FundingLandingDiagnostic;